import { useCallback, useEffect, useState } from 'react';
import type { WsShellRecent } from './shellTypes';

const MAX_RECENTS = 8;

interface Stored {
  favorites: string[];
  recents: WsShellRecent[];
}

const EMPTY: Stored = { favorites: [], recents: [] };

const keyFor = (userKey: string) => `ws.shell.v1.${userKey}`;

function load(userKey: string | undefined): Stored {
  if (!userKey) return EMPTY;
  try {
    const raw = window.localStorage.getItem(keyFor(userKey));
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw) as Partial<Stored>;
    return {
      favorites: Array.isArray(parsed.favorites)
        ? parsed.favorites.filter((x): x is string => typeof x === 'string')
        : [],
      recents: Array.isArray(parsed.recents)
        ? parsed.recents
            .filter((r) => r && typeof r.label === 'string' && typeof r.path === 'string')
            .slice(0, MAX_RECENTS)
        : [],
    };
  } catch {
    return EMPTY; // blocked or corrupt storage: the navigation still works, it just forgets
  }
}

function save(userKey: string | undefined, value: Stored) {
  if (!userKey) return;
  try {
    window.localStorage.setItem(keyFor(userKey), JSON.stringify(value));
  } catch {
    /* private window or full storage: nothing to do */
  }
}

/**
 * Favourite applications and recently visited screens for the navigation drawer, kept in
 * `localStorage` per person (docs/ui-kit-app-shell-plan.md, decision 4). Local to this browser
 * for now; a server-side preferences table would let them follow the person between machines.
 *
 * Every read and write is guarded: when storage is blocked the hook still returns working state,
 * it just does not remember it. Without a `userKey` nothing is read or written.
 */
export function useShellPrefs(userKey: string | undefined) {
  const [state, setState] = useState<Stored>(() => load(userKey));

  // A different person signing in on the same browser gets their own lists.
  useEffect(() => {
    setState(load(userKey));
  }, [userKey]);

  const toggleFavorite = useCallback(
    (id: string) =>
      setState((prev) => {
        const next = {
          ...prev,
          favorites: prev.favorites.includes(id)
            ? prev.favorites.filter((x) => x !== id)
            : [...prev.favorites, id],
        };
        save(userKey, next);
        return next;
      }),
    [userKey],
  );

  /** Newest first, one entry per path, capped. */
  const recordVisit = useCallback(
    (visit: WsShellRecent) =>
      setState((prev) => {
        if (prev.recents[0]?.path === visit.path && prev.recents[0]?.label === visit.label) {
          return prev;
        }
        const next = {
          ...prev,
          recents: [visit, ...prev.recents.filter((r) => r.path !== visit.path)].slice(
            0,
            MAX_RECENTS,
          ),
        };
        save(userKey, next);
        return next;
      }),
    [userKey],
  );

  return {
    favoriteIds: state.favorites,
    toggleFavorite,
    recents: state.recents,
    recordVisit,
  };
}
